import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  onEnter?: () => void;
  length?: number;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  'aria-label'?: string;
}

const sanitizeDigits = (value: string, length: number) =>
  value.replace(/\D/g, '').slice(0, length);

export function VerificationCodeInput({
  value,
  onChange,
  onComplete,
  onEnter,
  length = 6,
  className,
  disabled = false,
  autoFocus = false,
  name,
  'aria-label': ariaLabel,
}: VerificationCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const normalizedValue = sanitizeDigits(value, length);
  const digits = Array.from(
    { length },
    (_, index) => normalizedValue[index] ?? '',
  );

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (normalizedValue !== value) {
      onChange(normalizedValue);
    }
  }, [normalizedValue, onChange, value]);

  const emitChange = (nextValue: string) => {
    const sanitizedValue = sanitizeDigits(nextValue, length);
    onChange(sanitizedValue);
    if (sanitizedValue.length === length) {
      onComplete?.(sanitizedValue);
    }
  };

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const handleDigitChange = (index: number, rawValue: string) => {
    const incomingDigits = rawValue.replace(/\D/g, '');

    if (!incomingDigits) {
      const nextDigits = [...digits];
      nextDigits[index] = '';
      emitChange(nextDigits.join(''));
      return;
    }

    const nextDigits = [...digits];
    let nextIndex = index;

    for (const digit of incomingDigits) {
      if (nextIndex >= length) {
        break;
      }
      nextDigits[nextIndex] = digit;
      nextIndex += 1;
    }

    emitChange(nextDigits.join(''));
    focusInput(Math.min(nextIndex, length - 1));
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        const nextDigits = [...digits];
        nextDigits[index] = '';
        emitChange(nextDigits.join(''));
        return;
      }

      if (index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
      return;
    }

    if (event.key === 'Enter') {
      onEnter?.();
    }
  };

  const handlePaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
    const pastedValue = sanitizeDigits(
      event.clipboardData.getData('text'),
      length,
    );

    if (!pastedValue) {
      return;
    }

    if (pastedValue.length === length) {
      emitChange(pastedValue);
      focusInput(length - 1);
      return;
    }

    const nextDigits = [...digits];
    let nextIndex = index;

    for (const digit of pastedValue) {
      if (nextIndex >= length) {
        break;
      }

      nextDigits[nextIndex] = digit;
      nextIndex += 1;
    }

    emitChange(nextDigits.join(''));
    focusInput(Math.max(index, Math.min(nextIndex, length) - 1));
  };

  return (
    <div className={twMerge('flex justify-center gap-2', className)}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          name={name}
          value={digit}
          disabled={disabled}
          aria-label={ariaLabel ?? `Verification code digit ${index + 1}`}
          onChange={(event) => handleDigitChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onPaste={(event) => handlePaste(index, event)}
          className={twMerge(
            'h-12 w-12 rounded-lg border border-neutral-700 bg-transparent text-center text-xl font-mono text-white outline-none transition-colors',
            'focus:border-neutral-300 hover:border-neutral-500',
            digit && 'border-green-600',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
        />
      ))}
    </div>
  );
}
